<template>
  <div
    v-if="isOpen"
    id="task_resources_popup"
    ref="task_resources_popup"
    v-click-outside="{ handler: hideResourcesListForTask }"
    :class="['gantt_grid_editor_placeholder', $style.task_resources_popup]"
  >
    <div
      :class="[$style.task_resources_container,

               topShadow ? $style.top_shadow : '',]"
    >
      <div
        v-if="hasSearch"
        :class="[$style.resource_search_container,
        ]"
      >
        <div :class="$style.search_icon">
          <svg-sprite
            name="search"
            type="regular"
            :size="24"
            color="#B2B2B2"
          />
        </div>

        <vgp-input
          v-model="searchText"
          :class="$style.search_input"
          :border="false"
          :placeholder="locales('resource_search_input_placeholder')"
          :select-by-focus="false"
          :initial-focus="true"
          type="text"
        />
      </div>

      <vgp-scroll-bar
        ref="scrollContainer"
        :height="resourcesContainerHeight + 'px'"
        @onScroll="calcPopupShadow"
      >
        <div
          v-if="filteredResources.length"
          :class="$style.resource_multiselect_list"
        >
          <div
            v-for="item in filteredResources"
            :key="item.isDivider ? item.label : item.resource_id"
            :class="$style.task_resource_item"
          >
            <div
              v-if="item.isDivider"
              :class="$style.divider_item_container"
            >
              <span :class="$style.divider_caption">
                {{ locales(item.label) }}
              </span>

              <span :class="$style.divider_underline" />
            </div>

            <task-resource-item
              v-else
              :task-id="taskId"
              :resource-id="item.resource_id"
              :resource-type="item.type"
              :selected="item.selected"
              :picture="item.picture"
              :name="item.name"
              :value-per-day="item.valuePerDay"
              :value="item.value"
              :work-days-count="item.workDaysCount"
              :custom-days-count="item.customDaysCount"
              :task-type="item.taskType"
              :gantt-id="ganttId"
              :focused="focusedId === item.resource_id"
              @onClickResource="onSelect"
              @onChange="onChange"
            />
          </div>
        </div>

        <div
          v-else-if="resourcesList.length"
          :class="$style.message_container"
          :style="{ height: resourcesContainerHeight + 'px' }"
        >
          <span :class="$style.nomatches_message_text">
            {{ locales('no_matches_message_text') }}
          </span>
        </div>

        <div
          v-else
          :class="$style.message_container"
          :style="{ height: resourcesContainerHeight + 'px' }"
        >
          <span :class="$style.empty_assign_message_text">
            {{ locales('empty_assign_popup_text') }}
          </span>
        </div>
      </vgp-scroll-bar>

      <div
        :class="[
          $style.resource_popup_buttons,
          $style.resource_shadow,
          bottomShadow ? $style.bottom_shadow : '',
        ]"
      >
        <div :class="$style.buttons">
          <vgp-button
            v-if="hasReset"
            type="text"
            :small="true"
            :label="locales('unassigned_action')"
            @onClick="onReset"
          />
        </div>

        <div :class="$style.buttons">
          <vgp-button
            v-if="hasAddPeople"
            type="secondary"
            :small="true"
            :label="locales('add_people_from_grid')"
            @onClick="onAddPeople"
          />

          <vgp-button
            :style="{ marginLeft: 12 + 'px' }"
            type="primary"
            :small="true"
            :label="locales('common_ok')"
            @onClick="hideResourcesListForTask"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import app from '$$app';
import globalStore from '$$globalStore';
import routerHelper from '$$routerHelper';
import svgSprite from '$$vueCmp/VueIcon/svgSprite.vue';
import vgpInput from '$$vueCmp/vInput/vgpInput.vue';
import vgpButton from '$$vueCmp/globalButton/vgpButton.vue';
import vgpScrollBar from '$$vueCmp//scrollBar/vgpScrollBar.vue';
import constants from '$$helpers/constants';
import projectsModel from '$$projectModel';
import rights from '../../components/rights';
import TaskResourceItem from './taskResourceItem.vue';
import taskResourcesHelpers from './helpers';

const RESOURCES_PER_PAGE_COUNT = 6;

export default {
  name: 'TaskResourcesPopup',
  components: {
    TaskResourceItem,
    vgpScrollBar,
    svgSprite,
    vgpInput,
    vgpButton,
  },
  data() {
    return {
      locales: __,
      isOpen: false,
      eventsListeners: [],
      resourcesList: [],
      searchText: '',
      hasSearch: false,
      hasAddPeople: false,
      topShadow: false,
      bottomShadow: false,
      taskId: null,
      ganttId: null,
      sourceType: null,
      closeCallback: null,
      focusedId: null,
      currentPosition: null,
      pendingEnter: false,
    };
  },
  computed: {
    filteredResources() {
      const searchText = this.searchText;
      const resourcesList = this.resourcesList;

      const foundResources = _.filter(resourcesList, resource => resource.name.toLowerCase().includes(searchText.toLowerCase()));
      const groupedResources = _.groupBy(foundResources, resource => (_.isNull(resource.userId) ? 'material' : 'people'));

      return [
        ...groupedResources.people ? [{ isDivider: true, label: 'resources_layout_header_title_1' }] : [],
        ...groupedResources.people || [],
        ...groupedResources.material ? [{ isDivider: true, label: 'resources_layout_header_title_2' }] : [],
        ...groupedResources.material || [],
      ];
    },

    hasReset() {
      return this.resourcesList.some(resource => resource.selected);
    },

    resourcesContainerHeight() {
      const resourcesList = this.resourcesList;
      const itemHeight = 32;
      const typesCount = _.uniqBy(resourcesList, item => !!item.userId).length;
      const BOTTOM_MARGIN = 30;
      const defaultItemsHeight = 96;

      if (resourcesList.length === 0) {
        return itemHeight * 2;
      }

      if (window.innerHeight < 768 && this.currentPosition && this.currentPosition.top !== undefined && this.currentPosition.bottom !== undefined) {
        const spaceBelow = window.innerHeight - this.currentPosition.bottom - BOTTOM_MARGIN - defaultItemsHeight;
        const spaceAbove = this.currentPosition.top - BOTTOM_MARGIN - defaultItemsHeight;

        const availableSpace = Math.max(spaceBelow, spaceAbove);

        if (availableSpace <= 0) {
          return 0;
        }

        const maxRows = Math.floor(availableSpace / itemHeight);
        const itemsToShow = Math.min(resourcesList.length, RESOURCES_PER_PAGE_COUNT);
        const rowsToShow = Math.min(itemsToShow + typesCount, maxRows);

        let height = Math.max(rowsToShow, Math.ceil(defaultItemsHeight / itemHeight)) * itemHeight;

        height = Math.min(height, defaultItemsHeight);

        return height;
      }

      const itemsToShow = Math.min(resourcesList.length, RESOURCES_PER_PAGE_COUNT);

      return (itemsToShow + typesCount) * itemHeight;
    },
  },
  watch: {
    filteredResources: {
      handler() {
        this.calcPopupShadow();
      },
    },
    searchText() {
      this.focusedId = null;
    },
  },
  created() {
    this.addEventListeners();
    document.addEventListener('keydown', this.onKeyDown);
  },
  beforeUnmount() {
    this.removeEventsListeners();
    document.removeEventListener('keydown', this.onKeyDown);
  },
  methods: {
    addEventListeners() {
      this.removeEventsListeners();

      this.eventsListeners.push(app.on('showResourcesListForTask', this.showResourcesListForTask.bind(this)));
      this.eventsListeners.push(app.on('hideResourcesListForTask', this.hideResourcesListForTask.bind(this)));
      this.eventsListeners.push(app.on('popup:show', this.hideResourcesListForTask.bind(this)));
    },

    removeEventsListeners() {
      this.eventsListeners.forEach(id => app.off(id));
      this.eventsListeners = [];
    },

    showResourcesListForTask: _.debounce(function (position, taskId, ganttId, sourceType, closeCallback = null) {
      this.hideResourcesListForTask();

      this.taskId = taskId;
      this.ganttId = ganttId;
      this.sourceType = sourceType;
      this.closeCallback = closeCallback;
      this.searchText = '';
      this.focusedId = null;

      const projectConfig = projectsModel.getProjectConfig(this.ganttId);

      this.resourcesList = taskResourcesHelpers.getResourcesListForPopup(this.taskId, this.ganttId);
      this.hasSearch = this.resourcesList.length > RESOURCES_PER_PAGE_COUNT;

      this.hasAddPeople = !projectConfig.isJira && rights.project.hasRight(this.ganttId, 'resources_page') && !routerHelper.isMultiProjectsRoute()
        && !routerHelper.isListViewRoute() && !routerHelper.isWorkloadViewRoute() && !routerHelper.getCurrentRoute().path.includes('/comments');

      this.isOpen = true;
      this.currentPosition = position;
      this.$nextTick(() => {
        this.calcPopupPosition(position, sourceType);
        this.calcPopupShadow();

        taskResourcesHelpers.prepareEstimationsForResourcesList(this.taskId, this.ganttId, this.resourcesList)
          .then(newResourcesList => {
            this.resourcesList = newResourcesList;
          });
      });
    }),

    hideResourcesListForTask() {
      if (this.isOpen) {
        this.saveTaskResources();
      }

      this.isOpen = false;
      this.taskId = null;
      this.ganttId = null;
      this.sourceType = null;
      this.searchText = '';
      this.focusedId = null;
      this.pendingEnter = false;

      if (this.closeCallback) {
        this.closeCallback();
      }

      this.closeCallback = null;
    },

    calcPopupPosition(position, sourceType) {
      const popup = document.querySelector('#task_resources_popup');
      const bottomHeight = document.body.clientHeight - position.bottom;
      const rightWidth = document.body.clientWidth - position.left;
      const popupHeight = popup.clientHeight;
      const popupWidth = popup.clientWidth;

      const top = bottomHeight < popupHeight && sourceType !== 'taskView'
        ? position.top - popupHeight - 3
        : position.top + position.height;

      const left = rightWidth < popupWidth && sourceType !== 'taskView'
        ? position.right - popupWidth
        : position.left;

      popup.style.left = `${left}px`;
      popup.style.top = `${top}px`;

      this.$refs.scrollContainer?.$el?.scrollTo({ top: 0 });
    },

    calcPopupShadow: _.debounce(function () {
      const container = document.querySelector('#task_resources_popup .simplebar-content-wrapper');

      this.topShadow = container && container.scrollHeight > container.clientHeight && container.scrollTop > 0;
      this.bottomShadow = container && container.scrollHeight > container.clientHeight && (container.scrollHeight - container.clientHeight) !== container.scrollTop;
    }),

    onSelect(resourceId) {
      taskResourcesHelpers.resourcesListSelect(this.taskId, this.ganttId, resourceId, this.resourcesList)
        .then(newResourcesList => {
          this.resourcesList = newResourcesList;
        });
    },

    onChange(resourceId, value, valuePerDay) {
      taskResourcesHelpers.resourcesListChange(this.taskId, this.ganttId, resourceId, value, valuePerDay, this.resourcesList)
        .then(newResourcesList => {
          this.resourcesList = newResourcesList;
          this.pendingEnter && this.hideResourcesListForTask();
        });
    },

    onReset() {
      this.resourcesList = taskResourcesHelpers.resourcesListReset(this.resourcesList);
      this.searchText = '';
    },

    onAddPeople() {
      this.hideResourcesListForTask();
      routerHelper.pushByNameAndParams('project', { mode: 'resources' });
    },

    async saveTaskResources() {
      const changedResources = taskResourcesHelpers.prepareTaskResourcesForUpdate(this.taskId, this.ganttId, this.resourcesList);

      if (changedResources.length) {
        const tasksToUpdate = [{ id: this.taskId, changedResources }];

        if (this.sourceType === 'taskView') {
          userExtAnalytics.log('task_settings_action', { action: 'assigned', type: 'change' });
        }

        if (this.sourceType === 'gridView') {
          userExtAnalytics.log('task_grid_action', { action: 'assigned', type: 'change' });
        }

        setTimeout(() => app.trigger('onUpdateResourcesListForTask'));

        await globalStore.dispatch('tasksModel/updateTasks', {
          tasks: tasksToUpdate,
          sourceEvent: {
            type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_RESOURCES_UPDATE,
            sourceId: this.taskId,
          },
          fromGantt: false,
        });
      }
    },

    multiAssignHandler(isSelect, num = 0) {
      if (isSelect) {
        this.focusedId && this.onSelect(this.focusedId);

        return;
      }

      let pos = 0;

      _.each(this.filteredResources, (o, i) => {
        if (o.resource_id === this.focusedId) {
          pos = i;
        }
      });

      pos += num;

      if (this.filteredResources[pos]?.isDivider) {
        pos += num;
      }

      if (pos < 0) {
        pos = 0;
      }

      if (pos > this.filteredResources.length - 1) {
        pos = this.filteredResources.length - 1;
      }

      if (this.filteredResources[pos]?.resource_id) {
        this.focusedId = this.filteredResources[pos].resource_id;
      }
    },

    onKeyDown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.keyCode === 38) { // up
        const container = document.querySelector('#task_resources_popup .simplebar-content-wrapper');

        container && container.focus();
        this.multiAssignHandler(false, -1);
      }

      if (event.keyCode === 40) { // down
        const container = document.querySelector('#task_resources_popup .simplebar-content-wrapper');

        container && container.focus();
        this.multiAssignHandler(false, 1);
      }

      if (event.keyCode === 32) { // space
        event.preventDefault();
        this.multiAssignHandler(true);
      }

      if (event.keyCode === 27) { // esc
        this.hideResourcesListForTask();
      }

      if (event.keyCode === 13) { // enter
        if (event.target.tagName === 'INPUT') {
          this.pendingEnter = true;
        } else {
          this.hideResourcesListForTask();
        }
      }
    },
  },
};
</script>

<style module src="./styles.less" lang="less"></style>
